import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Copy, Archive, Save, Plus, AlertTriangle, Info } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import { pitchesAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { formatDateTimeWithZone } from '../lib/format.js';

// PITCH LIBRARY — the shared script bundles campaigns can run.
//
// DUPLICATE LEADS, ON PURPOSE. An A/B variant is almost always a copy of what is
// already running; starting from blank is how someone ships a pitch with no
// behaviour block and reintroduces the bleed this feature exists to stop. So
// "Duplicate" is the primary action on every row and "New pitch" is secondary.
//
// EDITING A LIVE PITCH SPLITS AN EXPERIMENT. `usedBy` lists the campaigns on a
// pitch right now, and core returns `versionChanged: true` on every save because the
// version is content-derived — calls before and after are different variants pooled
// under one name. When a pitch is live on an active campaign we push Duplicate-and-
// edit instead of a silent in-place save.

const FIELDS = [
  { key: 'body', label: 'Pitch body', rows: 10, hint: 'The script. Replaces the campaign’s own systemPromptOverride when this pitch is selected.' },
  { key: 'behaviorBlock', label: 'Behaviour block', rows: 5, hint: '⚠️ Injected AFTER the body under a header reading “THIS SECTION WINS” — it outranks the script, which is how a stale one silently overrode a new pitch.' },
  { key: 'openingLine', label: 'Opening line', rows: 2, hint: 'Spoken near-verbatim with no LLM round-trip. Leave blank to fall back to the campaign’s own.' },
  { key: 'dtmfRules', label: 'Phone-menu rules', rows: 3, hint: 'What to press on an IVR. Leave blank to fall back to the campaign’s own.' }
];

const EMPTY = { name: '', notes: '', body: '', behaviorBlock: '', openingLine: '', dtmfRules: '' };

export default function Pitches() {
  const { user } = useAuth();
  const canWrite = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await pitchesAPI.list(showArchived);
      setItems(r?.data || r || []);
      setFailed(false);
    } catch (err) {
      // /pitches never 404s — this API authenticates before it routes, so a live
      // route and a bogus one both 401 unauthenticated and a real request 200s.
      // A failure here is a real failure; say so instead of claiming the feature
      // isn't on this server. (Charlie, 2026-08-03.)
      setFailed(true);
      setError(err.body?.message || err.message || 'Could not load pitches.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeUsers = (p) => (p.usedBy || []).filter((c) => c.status === 'active');

  const openEditor = (p) => {
    setOpenId(p.id);
    setDraft({ ...EMPTY, ...p, notes: p.notes || '', behaviorBlock: p.behaviorBlock || '', openingLine: p.openingLine || '', dtmfRules: p.dtmfRules || '' });
    setStatus('');
  };

  const save = async () => {
    if (!draft) return;
    // Splitting a running experiment is a decision, not a side effect of clicking Save.
    const live = activeUsers(draft);
    if (live.length && !window.confirm(
      `“${draft.name}” is live on ${live.map((c) => c.name).join(', ')}.\n\n`
      + 'Saving changes it for those campaigns immediately, and calls before and after this edit '
      + 'become different variants pooled under one name — which ruins an A/B comparison.\n\n'
      + 'Duplicate instead if you are testing a variant. Save anyway?'
    )) return;
    try {
      const r = await pitchesAPI.update(draft.id, {
        name: draft.name,
        notes: draft.notes || null,
        body: draft.body,
        behaviorBlock: draft.behaviorBlock || null,
        openingLine: draft.openingLine || null,
        dtmfRules: draft.dtmfRules || null
      });
      const saved = r?.data || r;
      setStatus(`Saved. Version is now ${saved.version} — calls from here on run this variant.`);
      await load();
      setDraft((d) => ({ ...d, ...saved }));
    } catch (err) {
      const issues = err.body?.issues;
      setError(Array.isArray(issues) ? issues.join(' · ') : (err.body?.message || err.message || 'Save failed.'));
    }
  };

  const duplicate = async (p) => {
    try {
      const r = await pitchesAPI.clone(p.id, { name: `${p.name} (copy)` });
      await load();
      openEditor(r?.data || r);
      setStatus('Duplicated. Edit the copy — the original keeps running unchanged.');
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not duplicate.');
    }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive “${p.name}”? It stops being selectable for new campaigns. Existing history is kept.`)) return;
    try {
      await pitchesAPI.archive(p.id);
      await load();
    } catch (err) {
      // Core refuses while it governs an active campaign, and names them.
      setError(err.status === 409
        ? (err.body?.message || 'That pitch is still live on an active campaign — move those campaigns off it first.')
        : (err.body?.message || err.message || 'Could not archive.'));
    }
  };

  const create = async () => {
    try {
      const r = await pitchesAPI.create({
        name: newDraft.name,
        notes: newDraft.notes || null,
        body: newDraft.body,
        behaviorBlock: newDraft.behaviorBlock || null,
        openingLine: newDraft.openingLine || null,
        dtmfRules: newDraft.dtmfRules || null
      });
      setCreating(false);
      setNewDraft(EMPTY);
      await load();
      openEditor(r?.data || r);
    } catch (err) {
      const issues = err.body?.issues;
      setError(Array.isArray(issues) ? issues.join(' · ') : (err.body?.message || err.message || 'Could not create.'));
    }
  };

  if (failed) {
    return (
      <div className="px-6 md:px-10 py-8 max-w-4xl">
        <PageHeader title="Pitches" />
        <section className="card">
          <div className="flex items-start gap-3">
            <Info size={18} className="mt-0.5 shrink-0 text-stone-500" />
            <p className="text-sm text-stone-400">
              {error || 'The pitch library could not be loaded. Refresh to try again.'}
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-10 py-8 max-w-5xl">
      <PageHeader
        title="Pitches"
        subtitle="Shared script bundles — body, behaviour block, phone-menu rules and opening line — that a campaign runs as one unit."
        actions={canWrite && (
          <button type="button" className="btn-ghost" onClick={() => setCreating((v) => !v)}>
            <Plus size={14} /> New pitch
          </button>
        )}
      />

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}
      {status && <p className="mb-4 text-sm text-emerald-300">{status}</p>}

      <p className="mb-4 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>
          Selecting a pitch is <span className="text-stone-100">recorded but not yet spoken</span> —
          the call agent still reads each campaign’s own fields. Editing here is safe; it just won’t
          change a live call until that wiring lands.
        </span>
      </p>

      {creating && (
        <section className="card mb-6 space-y-3">
          <h2 className="text-lg text-stone-100">New pitch</h2>
          <p className="text-sm text-stone-400">
            Starting blank is how a pitch ships with no behaviour block. If you’re testing a variant of
            something that already works, close this and use <span className="text-stone-200">Duplicate</span> instead.
          </p>
          <label className="block">
            <span className="field-label">Name</span>
            <input className="field-input" value={newDraft.name} onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })} placeholder="e.g. Carriers — process first" />
          </label>
          <label className="block">
            <span className="field-label">Pitch body</span>
            <textarea className="field-input font-mono text-xs" rows={8} value={newDraft.body} onChange={(e) => setNewDraft({ ...newDraft, body: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create} disabled={!newDraft.name.trim() || !newDraft.body.trim()}>Create</button>
            <button type="button" className="btn-ghost" onClick={() => { setCreating(false); setNewDraft(EMPTY); }}>Cancel</button>
          </div>
        </section>
      )}

      <label className="mb-3 inline-flex items-center gap-2 text-sm text-stone-400">
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        Show archived
      </label>

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <section className="card"><p className="text-sm text-stone-400">No pitches yet. Campaigns are using their own script fields.</p></section>
      ) : (
        <div className="space-y-3">
          {items.map((p) => {
            const live = activeUsers(p);
            const missing = ['body', 'behaviorBlock', 'openingLine', 'dtmfRules'].filter((f) => !(p.defines || []).includes(f));
            const open = openId === p.id;
            return (
              <section key={p.id} className="card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <FileText size={15} className="text-alfred-gold shrink-0" />
                      <h3 className="text-stone-100">{p.name}</h3>
                      {p.archived && <span className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px]">archived</span>}
                      {live.length > 0 && (
                        <span className="badge bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px]">
                          live on {live.length}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-stone-500">
                      <code className="text-alfred-gold/70">{p.version}</code>
                      {p.updatedAt && <> · updated {formatDateTimeWithZone(p.updatedAt)}</>}
                    </div>
                    {p.notes && <p className="mt-1 text-sm text-stone-400">{p.notes}</p>}
                    {missing.length > 0 && (
                      <p className="mt-1 text-xs text-amber-200">
                        Doesn’t set {missing.join(', ')} — campaigns keep their own value for {missing.length === 1 ? 'that' : 'those'}.
                      </p>
                    )}
                    {live.length > 0 && (
                      <p className="mt-1 text-xs text-stone-500">On: {live.map((c) => c.name).join(', ')}</p>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      {/* Duplicate first — the safe default for a variant. */}
                      <button type="button" className="btn-primary py-1" onClick={() => duplicate(p)}>
                        <Copy size={13} /> Duplicate
                      </button>
                      <button type="button" className="btn-ghost py-1" onClick={() => (open ? setOpenId(null) : openEditor(p))}>
                        {open ? 'Close' : 'Edit'}
                      </button>
                      {!p.archived && (
                        <button type="button" className="btn-ghost py-1 text-red-300 hover:text-red-200" onClick={() => archive(p)}>
                          <Archive size={13} /> Archive
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {open && draft && (
                  <div className="mt-4 space-y-3 border-t border-stone-800 pt-4">
                    {live.length > 0 && (
                      <p className="flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        Live on {live.map((c) => c.name).join(', ')}. Saving changes those campaigns now and
                        splits calls into two variants under one name. Duplicate if you’re testing.
                      </p>
                    )}
                    <label className="block">
                      <span className="field-label">Name</span>
                      <input className="field-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                    </label>
                    <label className="block">
                      <span className="field-label">Notes (not spoken)</span>
                      <input className="field-input" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="What this variant is testing" />
                    </label>
                    {FIELDS.map((f) => (
                      <label key={f.key} className="block">
                        <span className="field-label">{f.label}</span>
                        <textarea
                          className="field-input font-mono text-xs"
                          rows={f.rows}
                          value={draft[f.key] || ''}
                          onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        />
                        <span className="mt-1 block text-xs text-stone-500">{f.hint}</span>
                      </label>
                    ))}
                    <button type="button" className="btn-primary" onClick={save}>
                      <Save size={14} /> Save pitch
                    </button>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-xs text-stone-500">
        Set which pitch a campaign runs from the campaign’s own page —{' '}
        <Link to="/campaigns" className="text-alfred-gold hover:underline">Campaigns</Link>.
      </p>
    </div>
  );
}
